import isObject from 'lodash/isObject';
import isArray from 'lodash/isArray';
import get from 'lodash/get';
import flatten from 'lodash/flatten';
import isNil from 'lodash/isNil';
import cloneDeep from 'lodash/cloneDeep';
import surveyUrls from './surveyUrls';
import FhirPropertyParser from '../../../util/fhirResourceParsers/FhirPropertyParser';
import {
  FhirBoolean,
  Coding,
  Date as FhirDate,
  FhirDateTime,
  Decimal,
  Integer,
  Quantity,
  FhirString,
  Time,
  URI,
  FhirArray,
} from '../../../util/fhirResourceParsers/Types';
import ExtensionCollection from './ExtensionCollection';

export type ResponseValueTypes =
  | 'valueBoolean'
  | 'valueDecimal'
  | 'valueInteger'
  | 'valueDate'
  | 'valueDateTime'
  | 'valueTime'
  | 'valueString'
  | 'valueUri'
  | 'valueAttachment'
  | 'valueCoding'
  | 'valueQuantity';

const copyAndShift = (source: any[] = []) => {
  const copied = [...source];
  copied.shift();
  return copied;
};

/**
 * Repeating groups (`repeats: true` on a group questionnaire item) are
 * answered as multiple instances. Response paths address a single instance
 * with an index suffix on the group's segment, e.g.
 * `["page", "sisters[2]", "sister-age"]`. Segments without a suffix behave
 * exactly as before.
 */
const INSTANCE_SEGMENT_REGEX = /^(.+)\[(\d+)\]$/;

export type ParsedPathSegment = {
  link: string;
  instanceIndex: number | null;
};

export const parsePathSegment = (segment: string): ParsedPathSegment => {
  const match = INSTANCE_SEGMENT_REGEX.exec(segment);

  if (!match) {
    return { link: segment, instanceIndex: null };
  }

  return { link: match[1], instanceIndex: Number(match[2]) };
};

export const buildInstanceSegment = (
  link: string,
  instanceIndex: number,
): string => `${link}[${instanceIndex}]`;

type FhirResponseWithoutItem = Omit<fhir.QuestionnaireResponse, 'item'>;
type FhirResponseItemWithoutItem = Pick<
  fhir.QuestionnaireResponseItem,
  Exclude<keyof fhir.QuestionnaireResponseItem, 'item'>
>;

export class ResponseUpdate {
  _updatePath: string[];
  _valueType: ResponseValueTypes;
  _value: any;

  constructor(updatePath: string[], valueType: ResponseValueTypes, value: any) {
    this._updatePath = updatePath;
    this._valueType = valueType;
    this._value = value;
  }

  get updatePath(): string[] {
    return this._updatePath;
  }

  get valueType(): ResponseValueTypes {
    return this._valueType;
  }

  get value(): any {
    return this._value;
  }
}

export type ParentResponseItemSubItems = { [link: string]: ResponseItem };
export type LeafSubItems = 'no-sub-items';
export type ResponseItemSubItems = ParentResponseItemSubItems | LeafSubItems;

export type FillProvenance = {
  source: string;
  citations: string[];
};

export class ResponseItem {
  static initEmptyResponseItem(
    linkId: string,
    valueType: ResponseValueTypes,
  ): ResponseItem {
    return new ResponseItem(linkId, 'no-sub-items', valueType);
  }

  /**
   * Initialize the container item for a repeating group.  The container holds
   * one ResponseItem per answered instance (all sharing the group's linkId)
   * and serializes back to repeated same-linkId fhir items.
   */
  static initRepeatingGroupItem(
    linkId: string,
    instances: ResponseItem[] = [],
  ): ResponseItem {
    const containerItem = new ResponseItem(linkId, {});
    containerItem.instances = instances;
    return containerItem;
  }

  /**
   * Initialize a response item and its children items from the item backbone element of a fhir resource
   */
  static initFromFhirResponseItem(
    fhirResponseItem: fhir.QuestionnaireResponseItem,
    repeatingSurveyItems: string[] = [],
    repeatingGroupItems: string[] = [],
  ): ResponseItem {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!fhirResponseItem || !isObject(fhirResponseItem)) return null;

    const {
      linkId,
      item,
      answer = [] as fhir.QuestionnaireResponseItemAnswer[],
    } = fhirResponseItem;

    // linkid requeired
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!linkId) return null;

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    let valueType: ResponseValueTypes = null;
    const arrayValue: any[] = [];
    const answerExtensions: fhir.Extension[][] = [];

    answer.forEach(
      (singleAnswer: fhir.QuestionnaireResponseItemAnswer = {}) => {
        const {
          valueBoolean,
          valueDecimal,
          valueInteger,
          valueDate,
          valueDateTime,
          valueTime,
          valueString,
          valueUri,
          valueCoding,
          valueQuantity,
          valueAttachment,
        } = singleAnswer;

        if (valueBoolean !== undefined) {
          valueType = 'valueBoolean';
          arrayValue.push(valueBoolean);
          answerExtensions.push(singleAnswer.extension || []);
        } else if (valueDecimal !== undefined) {
          valueType = 'valueDecimal';
          arrayValue.push(valueDecimal);
          answerExtensions.push(singleAnswer.extension || []);
        } else if (valueInteger !== undefined) {
          valueType = 'valueInteger';
          arrayValue.push(valueInteger);
          answerExtensions.push(singleAnswer.extension || []);
        } else if (valueDate !== undefined) {
          valueType = 'valueDate';
          arrayValue.push(valueDate);
          answerExtensions.push(singleAnswer.extension || []);
        } else if (valueDateTime !== undefined) {
          valueType = 'valueDateTime';
          arrayValue.push(valueDateTime);
          answerExtensions.push(singleAnswer.extension || []);
        } else if (valueTime !== undefined) {
          valueType = 'valueTime';
          arrayValue.push(valueTime);
          answerExtensions.push(singleAnswer.extension || []);
        } else if (valueString !== undefined) {
          valueType = 'valueString';
          arrayValue.push(valueString);
          answerExtensions.push(singleAnswer.extension || []);
        } else if (valueUri !== undefined) {
          valueType = 'valueUri';
          arrayValue.push(valueUri);
          answerExtensions.push(singleAnswer.extension || []);
        } else if (valueCoding !== undefined) {
          valueType = 'valueCoding';
          arrayValue.push(valueCoding);
          answerExtensions.push(singleAnswer.extension || []);
        } else if (valueQuantity !== undefined) {
          valueType = 'valueQuantity';
          arrayValue.push(valueQuantity);
          answerExtensions.push(singleAnswer.extension || []);
        } else if (valueAttachment !== undefined) {
          valueType = 'valueAttachment';
          arrayValue.push(valueAttachment);
          answerExtensions.push(singleAnswer.extension || []);
        }
      },
    );

    const repeats = repeatingSurveyItems.includes(linkId);

    if (arrayValue.length > 1 && !repeats) {
      console.error(
        'Received a questionnaire response with a repeating value on a non-repeating survey item.  Extra values will be ignored',
      );
    }

    const value = repeats ? arrayValue : arrayValue[0];

    // recursively initialize child response items
    const subItems: { [id: string]: ResponseItem } =
      item && isArray(item)
        ? initSubItemsFromFhirItems(
            item,
            repeatingSurveyItems,
            repeatingGroupItems,
          )
        : {};

    const { item: ommittedItem, ...responseItemWithoutSubItems } =
      fhirResponseItem;
    return new ResponseItem(
      linkId,
      subItems,
      valueType,
      value,
      responseItemWithoutSubItems,
      answerExtensions,
    );
  }

  link: string;
  valueType: ResponseValueTypes;
  value: any;
  subItems: ResponseItemSubItems;
  restOfItemData: FhirResponseItemWithoutItem;
  answerExtensions: fhir.Extension[][];

  /**
   * Non-null only for repeating group containers (see initRepeatingGroupItem).
   * Each entry is a plain group ResponseItem representing one answered
   * instance, all sharing this item's link.
   */
  instances: ResponseItem[] | null = null;

  constructor(
    link: string,
    subItems: ResponseItemSubItems,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    valueType: ResponseValueTypes = null,
    value?: any,
    restOfItemData?: FhirResponseItemWithoutItem,
    answerExtensions: fhir.Extension[][] = [],
  ) {
    this.link = link;
    this.valueType = valueType;
    this.value = isNil(value) ? null : value;
    this.subItems = subItems;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this.restOfItemData = restOfItemData;
    this.answerExtensions = answerExtensions;
  }

  /**
   * Does this response item represent a grouping as opposed to a value
   *
   * @readonly
   * @memberof ResponseItem
   */
  get isGroupResponseItem(): boolean {
    return this.valueType === null;
  }

  /**
   * Is this item the container for a repeating group's instances
   */
  get isRepeatingGroupItem(): boolean {
    return this.instances !== null;
  }

  /**
   * The number of instances of a repeating group container.  Always 0 for
   * non-repeating items.
   */
  get instanceCount(): number {
    return this.instances ? this.instances.length : 0;
  }

  get hasResponse(): boolean {
    return this.isArrayValue
      ? this.value !== null && this.value && this.value.length > 0
      : this.value !== null;
  }

  get isArrayValue(): boolean {
    return isArray(this.value);
  }

  get fillProvenance(): FillProvenance | null {
    const allExtensions = flatten(this.answerExtensions || []);
    const filledBy = allExtensions.find(
      extension => extension.url === surveyUrls.filledBy,
    );
    if (!filledBy?.valueString) return null;

    const citationValues = (extension: fhir.Extension): string[] => {
      const url = (extension.url || '').toLowerCase();
      const isCitation =
        extension.url !== surveyUrls.filledBy &&
        ['citation', 'reference', 'display', 'query'].some(part =>
          url.includes(part),
        );
      const ownValues = isCitation
        ? [
            extension.valueString,
            extension.valueUri,
            extension.valueReference?.display,
            extension.valueReference?.reference,
          ].filter(Boolean)
        : [];
      return [
        ...(ownValues as string[]),
        ...flatten((extension.extension || []).map(citationValues)),
      ];
    };

    return {
      source: filledBy.valueString,
      citations: Array.from(
        new Set(flatten(allExtensions.map(citationValues))),
      ),
    };
  }

  get subItemlist() {
    return Object.values(this._safeParentSubItems);
  }

  /**
   * All response items nested within the tree
   */
  get allTreeResponseItems(): ResponseItem[] {
    if (this.isRepeatingGroupItem) {
      return flatten([
        this,
        ...(this.instances || []).map(_ => _.allTreeResponseItems),
      ]);
    }

    return flatten([
      this,
      ...this.subItemlist.map(_ => _.allTreeResponseItems),
    ]);
  }

  private get _safeParentSubItems(): ParentResponseItemSubItems {
    return this.subItems === 'no-sub-items' ? {} : this.subItems || {};
  }

  getSubItemWithLink(linkId: string) {
    return this._safeParentSubItems[linkId] || null;
  }

  setSubItemWithLink(linkId: string, subItem: ResponseItem): void {
    if (this.subItems === 'no-sub-items')
      throw new Error(
        'Attempted to set a response sub item on a leaf survey response item',
      );

    this.subItems[linkId] = subItem;
  }

  removeSubItemWithLink(linkId: string): void {
    if (this.subItems !== 'no-sub-items') {
      delete this.subItems[linkId];
    }
  }

  /**
   * null values are not valid fhir.  Response items with null values should be filtered out
   */
  get isNullValuedLeafItem() {
    return this.subItems === 'no-sub-items' && this.value === null;
  }

  private _valueAsArray(): any[] {
    if (this.value === null) return [];

    return isArray(this.value) ? this.value : [this.value];
  }

  /**
   * Serializes to fhir response items.  A repeating group container produces
   * one item per instance (repeated same-linkId items, valid FHIR STU3); all
   * other items produce exactly one.
   */
  toFhirResponseItems(): fhir.QuestionnaireResponseItem[] {
    if (this.isRepeatingGroupItem) {
      return (this.instances || []).map(instance =>
        instance.toFhirResponseItem(),
      );
    }

    return [this.toFhirResponseItem()];
  }

  toFhirResponseItem(): fhir.QuestionnaireResponseItem {
    const subItems = flatten(
      Object.keys(this._safeParentSubItems).map(subItemLink => {
        const subItem = this.getSubItemWithLink(subItemLink);

        if (!subItem) return [];

        return subItem.toFhirResponseItems();
      }),
    ).filter(Boolean);

    const item = {
      ...this.restOfItemData,
      linkId: this.link,
    } as fhir.QuestionnaireResponseItem;

    if (subItems.length > 0) {
      item.item = subItems;
    }

    if (this.valueType) {
      item.answer = this._valueAsArray().map((value, index) => {
        const extensions = this.answerExtensions[index] || [];
        return {
          [this.valueType]: value,
          ...(extensions.length ? { extension: extensions } : {}),
        };
      });
    }

    return item;
  }

  get valueDisplay(): string {
    if (this.value === null) return '';

    const fhirResponseItem = this.toFhirResponseItem();

    const rawDisplayValue =
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      new FhirPropertyParser('resource.answer', FhirArray, {
        fhirArray: {
          optionalPathParsers: [
            {
              arrayElementPath: 'valueBoolean',
              arrayElementParser: new FhirBoolean(),
            },
            {
              arrayElementPath: 'valueCoding',
              arrayElementParser: new Coding(),
            },
            {
              arrayElementPath: 'valueDate',
              arrayElementParser: new FhirDate(),
            },
            {
              arrayElementPath: 'valueDateTime',
              arrayElementParser: new FhirDateTime(),
            },
            {
              arrayElementPath: 'valueDecimal',
              arrayElementParser: new Decimal(),
            },
            {
              arrayElementPath: 'valueInteger',
              arrayElementParser: new Integer(),
            },
            {
              arrayElementPath: 'valueQuantity',
              arrayElementParser: new Quantity(),
            },
            {
              arrayElementPath: 'valueString',
              arrayElementParser: new FhirString(),
            },
            {
              arrayElementPath: 'valueTime',
              arrayElementParser: new Time(),
            },
            {
              arrayElementPath: 'URI',
              arrayElementParser: new URI(),
            },
          ],
          concatenate: true,
        },
      }).getDisplayValue({ resource: fhirResponseItem }) || '';

    return String(rawDisplayValue);
  }
}

/**
 * Parses a level of fhir response items into a subItems dictionary.  Items
 * whose linkId identifies a repeating group are gathered (in document order)
 * into a single container ResponseItem holding one instance per fhir item;
 * all other items map 1:1 as before.
 */
function initSubItemsFromFhirItems(
  fhirItems: fhir.QuestionnaireResponseItem[],
  repeatingSurveyItems: string[],
  repeatingGroupItems: string[],
): ParentResponseItemSubItems {
  const subItems: ParentResponseItemSubItems = {};

  fhirItems
    .map(fhirItem =>
      ResponseItem.initFromFhirResponseItem(
        fhirItem,
        repeatingSurveyItems,
        repeatingGroupItems,
      ),
    )
    .filter(Boolean)
    .forEach(responseItem => {
      if (repeatingGroupItems.includes(responseItem.link)) {
        const existingContainer = subItems[responseItem.link];
        if (
          existingContainer &&
          existingContainer.isRepeatingGroupItem &&
          existingContainer.instances
        ) {
          existingContainer.instances.push(responseItem);
        } else {
          subItems[responseItem.link] = ResponseItem.initRepeatingGroupItem(
            responseItem.link,
            [responseItem],
          );
        }
      } else {
        subItems[responseItem.link] = responseItem;
      }
    });

  return subItems;
}

/**
 * An immutable class for storing response state and
 *
 * @export
 * @class SurveyResponse
 */
export default class SurveyResponse {
  static statuses = {
    inProgress: 'in-progress',
    completed: 'completed',
    amended: 'amended',
    enteredInError: 'entered-in-error',
    stopped: 'stopped',
  };

  /**
   * Initializes a survey response from a fhir questionnaire
   * @param {Object} fhirQuestionnaireResponse - A raw fhir questionnaire response
   * @param {String[]} repeatingSurveyItems - A list of repeating survey items.  This will determine if a item's value will be an array or non-array.
   * @param {String[]} repeatingGroupItems - The linkIds of repeating group items ("repeats: true" on groups).  Repeated same-linkId fhir items for these links are gathered into instance containers.
   */
  static initFromFhirQuestionnaireResponse(
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    fhirQuestionnaireResponse: fhir.QuestionnaireResponse = null,
    repeatingSurveyItems: string[] = [],
    repeatingGroupItems: string[] = [],
  ): SurveyResponse {
    if (!fhirQuestionnaireResponse || !isObject(fhirQuestionnaireResponse)) {
      return new SurveyResponse();
    }

    const { item } = fhirQuestionnaireResponse;

    const extensions = get<
      fhir.QuestionnaireResponse,
      'extension',
      fhir.Extension[]
    >(fhirQuestionnaireResponse, 'extension', [] as fhir.Extension[]);
    const lastVisitedPageExtension = extensions.find(
      (extension: fhir.Extension = {} as fhir.Extension) =>
        extension.url === surveyUrls.lastVisitedPage,
    );

    const lastVisitedPage = get<fhir.Extension, 'valueString', string>(
      lastVisitedPageExtension,
      'valueString',
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      null,
    );

    if (!item || !isArray(item)) {
      return new SurveyResponse({}, lastVisitedPage, fhirQuestionnaireResponse);
    }

    const responseItems: { [id: string]: ResponseItem } =
      initSubItemsFromFhirItems(
        item,
        repeatingSurveyItems,
        repeatingGroupItems,
      );

    return new SurveyResponse(
      responseItems,
      lastVisitedPage,
      fhirQuestionnaireResponse,
    );
  }

  /**
   * Determines if the given fhir questionnaire response is empty and not even partially completed by the user
   * @param {Object} fhirQuestionnaireResponse - A raw fhir questionnaire response
   */
  static isQuestionnaireResponseNotStarted(
    fhirQuestionnaireResponse: fhir.QuestionnaireResponse,
  ): boolean {
    if (!fhirQuestionnaireResponse) return true;

    const extensions = get<
      fhir.QuestionnaireResponse,
      'extension',
      fhir.Extension[]
    >(fhirQuestionnaireResponse, 'extension', [] as fhir.Extension[]);
    return extensions.some(
      (extension: fhir.Extension = {} as fhir.Extension) =>
        extension.url === surveyUrls.surveyNotStarted,
    );
  }

  /**
   * Gets the source questionnaire ID of the fhir questionnaire response
   * @param fhirQuestionnaireResponse - A raw fhir questionnaire response
   */
  static getSourceQuestionnaire(
    fhirQuestionnaireResponse: fhir.QuestionnaireResponse,
  ): string {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return fhirQuestionnaireResponse?.questionnaire?.reference
      ?.split('/')
      ?.at(-1);
  }

  /**
   * Gets the primary survey ID of the fhir questionnaire response
   * @param fhirQuestionnaireResponse - A raw fhir questionnaire response
   */
  static getPrimarySurveyId(
    fhirQuestionnaireResponse: fhir.QuestionnaireResponse,
  ): string {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return fhirQuestionnaireResponse?.meta.tag.find(
      _ => _?.system === surveyUrls.primarySurveyId,
    ).code;
  }

  responseItems: { [rootLink: string]: ResponseItem };
  lastVisitedPage: string;
  responseWithoutItem: FhirResponseWithoutItem;

  constructor(
    responseItems: { [rootLink: string]: ResponseItem } = {},
    lastVisitedPage?: string,
    responseWithoutItem?: FhirResponseWithoutItem,
  ) {
    this.responseItems = responseItems;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this.lastVisitedPage = lastVisitedPage || null;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this.responseWithoutItem = responseWithoutItem;
  }

  get isComplete() {
    const currentStatus = get(this.responseWithoutItem, 'status');
    return currentStatus === SurveyResponse.statuses.completed;
  }

  get isInProgress() {
    if (this.isComplete) return false;

    const extensionCollection = ExtensionCollection.initFromBackboneElement(
      this.responseWithoutItem,
    );
    return !extensionCollection.hasExtension(surveyUrls.surveyNotStarted);
  }

  get id() {
    return get(this.responseWithoutItem, 'id');
  }

  _clone(): SurveyResponse {
    // faster implementation possible, but for right now this should work
    const clonedItems = cloneDeep(this.responseItems);
    return new SurveyResponse(
      clonedItems,
      this.lastVisitedPage,
      this.responseWithoutItem,
    );
  }

  _recursiveGetItemAtPath(
    path: string[] = [],
    responseItem: ResponseItem,
  ): ResponseItem {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!responseItem) return null;

    if (path.length === 0) {
      return responseItem;
    } else {
      const { link, instanceIndex } = parsePathSegment(path[0]);
      let childItem = responseItem.getSubItemWithLink(link);

      // an instance segment ("sisters[2]") addresses one instance of a
      // repeating group container; a plain segment addresses the container
      if (instanceIndex !== null) {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        childItem =
          (childItem &&
            childItem.instances &&
            childItem.instances[instanceIndex]) ||
          null;
      }

      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      if (!childItem) return null;

      const copiedPath = [...path];
      copiedPath.shift();
      return this._recursiveGetItemAtPath(copiedPath, childItem);
    }
  }

  _getAllResponseItems(): ResponseItem[] {
    const recursiveGetItems = (responseItem: ResponseItem): ResponseItem[] => {
      if (responseItem.isRepeatingGroupItem) {
        return flatten([
          responseItem,
          ...(responseItem.instances || []).map(_ => recursiveGetItems(_)),
        ]);
      } else if (responseItem.isGroupResponseItem) {
        return flatten([
          responseItem,
          ...(responseItem.subItemlist || []).map(_ => recursiveGetItems(_)),
        ]);
      } else {
        return [responseItem];
      }
    };

    return flatten(
      Object.values(this.responseItems || {}).map(_ => recursiveGetItems(_)),
    );
  }

  get allResponseItems() {
    return this._getAllResponseItems();
  }

  get rootResponseItems() {
    return Object.values(this.responseItems);
  }

  get allAttachmentUrls(): string[] {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return this.allResponseItems
      .filter(_ => _.valueType === 'valueAttachment')
      .map(_ => (_.value as fhir.Attachment)?.url)
      .filter(Boolean);
  }

  get questionnaireName(): string {
    return this.responseWithoutItem?.questionnaire?.display || '';
  }

  get authoredDate(): Date | null {
    if (!this.responseWithoutItem?.authored) return null;

    const date = new Date(this.responseWithoutItem.authored);
    return isNaN(date.getTime()) ? null : date;
  }

  _getRootItem(segment: string): ResponseItem {
    const { link, instanceIndex } = parsePathSegment(segment);
    const rootItem = this.responseItems[link];

    if (instanceIndex === null) return rootItem;

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return (
      (rootItem && rootItem.instances && rootItem.instances[instanceIndex]) ||
      null
    );
  }

  getValueAtPath(path: string[] = []): any {
    const targetRoot = this._getRootItem(path[0]);
    const copiedPath = copyAndShift(path);
    const targetItem = this._recursiveGetItemAtPath(copiedPath, targetRoot);

    return targetItem === null ? null : targetItem.value;
  }

  /**
   * Gets the value of the specified linkId.  Note that this is not as performant as getValueAtPath
   * and should likely only be used for testing
   */
  getValue(linkId: string): any {
    const targetItem = this.allResponseItems.find(_ => _.link === linkId);
    return isNil(targetItem?.value) ? null : targetItem?.value;
  }

  getItemAtPath(path: string[] = []): ResponseItem {
    const targetRoot = this._getRootItem(path[0]);
    const copiedPath = copyAndShift(path);
    return this._recursiveGetItemAtPath(copiedPath, targetRoot);
  }

  /**
   * The number of answered instances of the repeating group container at the
   * given path (the path's last segment is the group's linkId, un-indexed).
   * Returns 0 when no instance has been answered yet.
   */
  getRepeatingGroupInstanceCount(path: string[] = []): number {
    const containerItem = this.getItemAtPath(path);
    return containerItem ? containerItem.instanceCount : 0;
  }

  /**
   * Returns a cloned response with a new empty instance appended to the
   * repeating group container at the given path.  Creates the container (and
   * any missing ancestors) if this is the group's first instance.
   */
  addRepeatingGroupInstance(path: string[] = []): SurveyResponse {
    const clonedSurveyResponse = this._clone();
    const groupLink = parsePathSegment(path[path.length - 1]).link;

    const ensureContainer = (
      parentSubItems: ParentResponseItemSubItems,
      remainingPath: string[],
    ): void => {
      const segment = remainingPath[0];
      const { link, instanceIndex } = parsePathSegment(segment);

      if (remainingPath.length === 1) {
        const existing = parentSubItems[link];
        const container =
          existing && existing.isRepeatingGroupItem
            ? existing
            : ResponseItem.initRepeatingGroupItem(link);
        (container.instances as ResponseItem[]).push(
          new ResponseItem(link, {}),
        );
        parentSubItems[link] = container;
        return;
      }

      // an instance segment ("sisters[1]") on an intermediate path addresses
      // one instance of a repeating group; the rest of the path continues
      // inside that instance (nested repeating groups)
      if (instanceIndex !== null) {
        const existing = parentSubItems[link];
        const container =
          existing && existing.isRepeatingGroupItem
            ? existing
            : ResponseItem.initRepeatingGroupItem(link);
        const instances = container.instances as ResponseItem[];
        while (instances.length <= instanceIndex) {
          instances.push(new ResponseItem(link, {}));
        }
        parentSubItems[link] = container;
        const instance = instances[instanceIndex];
        if (instance.subItems === 'no-sub-items') {
          throw new Error(
            `Cannot add a repeating group instance under leaf item ${link}`,
          );
        }
        ensureContainer(instance.subItems, remainingPath.slice(1));
        return;
      }

      const parent = parentSubItems[link] || new ResponseItem(link, {});
      parentSubItems[link] = parent;
      if (parent.subItems === 'no-sub-items') {
        throw new Error(
          `Cannot add a repeating group instance under leaf item ${link}`,
        );
      }
      ensureContainer(parent.subItems, remainingPath.slice(1));
    };

    if (path.length === 0 || !groupLink) {
      throw new Error(
        'addRepeatingGroupInstance requires a path ending at the repeating group linkId',
      );
    }

    ensureContainer(clonedSurveyResponse.responseItems, [...path]);
    return clonedSurveyResponse;
  }

  /**
   * Returns a cloned response with the instance at instanceIndex removed from
   * the repeating group container at the given path.  Later instances shift
   * down (their paths/indices change), matching UI removal semantics.
   */
  removeRepeatingGroupInstance(
    path: string[],
    instanceIndex: number,
  ): SurveyResponse {
    const clonedSurveyResponse = this._clone();
    const containerItem = clonedSurveyResponse.getItemAtPath(path);

    if (
      containerItem &&
      containerItem.isRepeatingGroupItem &&
      containerItem.instances
    ) {
      containerItem.instances.splice(instanceIndex, 1);
    }

    return clonedSurveyResponse;
  }

  updateFields(updates: ResponseUpdate[]): SurveyResponse {
    /**
     * Applies an update below an instance segment ("sisters[2]").  Ensures the
     * repeating group container and the addressed instance exist, recurses
     * into the instance with the index stripped from the path, and returns
     * the (possibly new) container.  Declared as a hoisted function because it
     * is mutually recursive with recursiveUpdate.
     */
    function updateInstanceChild(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      container: ResponseItem = null,
      instanceLink: string,
      instanceIndex: number,
      strippedPath: string[],
      valueType: ResponseValueTypes,
      value: any,
    ): ResponseItem {
      const safeContainer =
        container && container.isRepeatingGroupItem
          ? container
          : ResponseItem.initRepeatingGroupItem(instanceLink);
      const instances = safeContainer.instances as ResponseItem[];

      while (instances.length <= instanceIndex) {
        instances.push(new ResponseItem(instanceLink, {}));
      }

      instances[instanceIndex] = recursiveUpdate(
        instances[instanceIndex],
        strippedPath,
        valueType,
        value,
      );

      return safeContainer;
    }

    function recursiveUpdate(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      sourceItem: ResponseItem = null,
      path: string[],
      valueType: ResponseValueTypes,
      value: any,
    ): ResponseItem {
      if (path.length === 1) {
        return new ResponseItem(path[0], 'no-sub-items', valueType, value);
      }

      const link = path[0];
      const childPath = copyAndShift(path);
      const childSegment = childPath[0];
      const { link: childLink, instanceIndex } = parsePathSegment(childSegment);
      const childItem = sourceItem
        ? sourceItem.getSubItemWithLink(childLink)
        : null;

      // a plain (non-indexed) segment must not descend into a repeating group
      // container: writes would land on the container's subItems, which
      // serialization ignores (only instances are emitted) - silent data loss.
      // Callers must address an instance, e.g. "sisters[0]".
      if (
        instanceIndex === null &&
        childItem &&
        childItem.isRepeatingGroupItem
      ) {
        throw new Error(
          `updateFields path segment "${childSegment}" addresses repeating group "${childLink}" without an instance index (e.g. "${childLink}[0]")`,
        );
      }

      const newChild =
        instanceIndex !== null
          ? updateInstanceChild(
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              childItem,
              childLink,
              instanceIndex,
              [childLink, ...childPath.slice(1)],
              valueType,
              value,
            )
          : // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            recursiveUpdate(childItem, childPath, valueType, value);

      // if response item at this level has not been instantiated
      if (!sourceItem) {
        const subItems: ParentResponseItemSubItems = {};

        if (!newChild.isNullValuedLeafItem) {
          subItems[childLink] = newChild;
        }

        return new ResponseItem(link, subItems);
      } else if (!newChild.isNullValuedLeafItem) {
        sourceItem.setSubItemWithLink(childLink, newChild);
        return sourceItem;
      } else {
        sourceItem.removeSubItemWithLink(childLink);
        return sourceItem;
      }
    }

    const clonedSurveyResponse = this._clone();
    updates.forEach(update => {
      const rootSegment = update.updatePath[0];
      const { link: rootItemLink, instanceIndex } =
        parsePathSegment(rootSegment);
      const rootItem = clonedSurveyResponse.responseItems[rootItemLink];

      // same guard as recursiveUpdate for a repeating group at the root level
      if (
        instanceIndex === null &&
        rootItem &&
        rootItem.isRepeatingGroupItem &&
        update.updatePath.length > 1
      ) {
        throw new Error(
          `updateFields path segment "${rootSegment}" addresses repeating group "${rootItemLink}" without an instance index (e.g. "${rootItemLink}[0]")`,
        );
      }

      const updatedRoot =
        instanceIndex !== null
          ? updateInstanceChild(
              rootItem,
              rootItemLink,
              instanceIndex,
              [rootItemLink, ...update.updatePath.slice(1)],
              update.valueType,
              update.value,
            )
          : recursiveUpdate(
              rootItem,
              update.updatePath,
              update.valueType,
              update.value,
            );
      clonedSurveyResponse.responseItems[rootItemLink] = updatedRoot;
    });

    return clonedSurveyResponse;
  }

  /**
   * Returns the initial form value for the given root link.
   * The initial form value consists of all the leaf values of the form tree.
   *
   * @param {string} [rootLink=null] - The root page link.  If null, will get the whole form's value and not the paginated value
   * @returns {Object}
   * @memberof SurveyResponse
   */
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  getDefaultFormValues(rootLink: string = null): Record<string, any> {
    const recursiveGetLeafValues = (
      responseItem: ResponseItem,
      currentValue: Record<string, any> = {},
    ): Record<string, any> => {
      if (responseItem.isRepeatingGroupItem) {
        // a repeating group emits an array of per-instance value objects, e.g.
        // { sisters: [{ "sister-age": 44 }, { "sister-age": 47 }] }.  Field
        // names like "sisters[1].sister-age" then resolve naturally with
        // formik/lodash path semantics.
        return {
          ...currentValue,
          [responseItem.link]: (responseItem.instances || []).map(instance =>
            recursiveGetLeafValues(instance, {}),
          ),
        };
      } else if (responseItem.isGroupResponseItem) {
        let newCurValue = { ...currentValue };
        Object.keys(responseItem.subItems).forEach(subItemLink => {
          const subItem = responseItem.getSubItemWithLink(subItemLink);

          newCurValue = recursiveGetLeafValues(subItem, newCurValue);
        });

        return newCurValue;
      } else {
        return {
          ...currentValue,
          [responseItem.link]: responseItem.value,
        };
      }
    };

    const rootItems = rootLink
      ? [this.responseItems[rootLink]]
      : Object.keys(this.responseItems).map(link => this.responseItems[link]);

    let initialFormValues = {};
    rootItems.filter(Boolean).forEach(rootItem => {
      initialFormValues = recursiveGetLeafValues(rootItem, initialFormValues);
    });

    return initialFormValues;
  }

  /**
   * Returns a cloned survey response with the last visited root link.  It is useful to persists this so that a survey taker will resume at the last page they were on
   * @param {string} pageLink
   */
  updateLastVisitedPage(pageLink: string): SurveyResponse {
    return new SurveyResponse(
      this.responseItems,
      pageLink,
      this.responseWithoutItem,
    );
  }

  /**
   * Clones the survey response and marks it as complete
   *
   * @memberof SurveyResponse
   */
  setAsComplete(): SurveyResponse {
    const fhirDataWithCompleteStatus = {
      ...(this.responseWithoutItem || {}),
      status: SurveyResponse.statuses.completed,
    };

    return new SurveyResponse(
      this.responseItems,
      this.lastVisitedPage,
      fhirDataWithCompleteStatus,
    );
  }

  setAsInProgress(): SurveyResponse {
    const fhirDataWithInProgressStatus = {
      ...(this.responseWithoutItem || {}),
      status: SurveyResponse.statuses.inProgress,
    };

    return new SurveyResponse(
      this.responseItems,
      this.lastVisitedPage,
      fhirDataWithInProgressStatus,
    );
  }

  get itemsWithValues(): ResponseItem[] {
    return this._getAllResponseItems().filter(_ => _.hasResponse);
  }

  /**
   * If a survey is completed on behalf of the survey subject (ie: doctor filling out for subject) an author should be set
   */
  setAuthor(author: { reference?: string; display?: string }): SurveyResponse {
    const fhirDataWithAuthor = {
      ...(this.responseWithoutItem || ({} as FhirResponseWithoutItem)),
      author,
    };

    return new SurveyResponse(
      this.responseItems,
      this.lastVisitedPage,
      fhirDataWithAuthor,
    );
  }

  addAuthorExtension(reference: string): SurveyResponse {
    const extension = {
      url: 'https://lifeomic.com/fhir/author',
      valueString: reference,
      extension: [
        {
          url: 'https://lifeomic.com/fhir/authored',
          valueDateTime: new Date().toISOString(),
        },
      ],
    };
    const fhirDataWithAuthor = {
      ...(this.responseWithoutItem || ({} as FhirResponseWithoutItem)),
      extension: Array.isArray(this.responseWithoutItem?.extension)
        ? this.responseWithoutItem.extension.concat(extension)
        : [extension],
    };

    return new SurveyResponse(
      this.responseItems,
      this.lastVisitedPage,
      fhirDataWithAuthor,
    );
  }

  get sourceQuestionnaireId(): string | null {
    return SurveyResponse.getSourceQuestionnaire(this.responseWithoutItem);
  }

  get author(): string | null {
    return this.responseWithoutItem.author?.reference || null;
  }

  toFhirQuestionnaireResponse(): fhir.QuestionnaireResponse {
    // be sure to strip "survey not started" extension as it has atleast been updated by the user
    const { extension = [], ...nonExtensionFhirData } =
      this.responseWithoutItem || {};

    // remove not start and last visited page extensions
    const extensionsToKeep = extension.filter(
      (extension: fhir.Extension = {} as fhir.Extension) =>
        extension.url !== surveyUrls.surveyNotStarted &&
        extension.url !== surveyUrls.lastVisitedPage,
    );

    // only add extension if value exists
    if (this.lastVisitedPage) {
      extensionsToKeep.push({
        url: surveyUrls.lastVisitedPage,
        valueString: this.lastVisitedPage,
      });
    }

    const fhirResource = {
      ...nonExtensionFhirData,
      authored: this.isComplete
        ? nonExtensionFhirData.authored
        : new Date().toISOString(),
      item: flatten(
        Object.keys(this.responseItems).map(responseItemLink => {
          const responseItem = this.responseItems[responseItemLink];

          if (!responseItem) return [];

          return responseItem.toFhirResponseItems();
        }),
      ).filter(Boolean),
    } as fhir.QuestionnaireResponse;

    if (extensionsToKeep.length > 0) {
      fhirResource.extension = extensionsToKeep;
    }

    return fhirResource;
  }
}
